// @ts-nocheck
/**
 * Step 1 team entry — the single implementation used by BOTH the public
 * registration wizard and the organiser's "Add teams" dialog on the Live Sheet.
 *
 * Implements team-entry-spec.md: match-as-you-type after 3 characters,
 * an always-present "None of these, it's a new team" path, parseTeamName on the
 * new-team path, a plain-language confirmation, and the storage rule that a
 * canonical name never contains an age group, grad year or grade.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Plus, Trash2, Check } from "lucide-react";
import { toast } from "sonner";
import { CartItem } from "@/types/registration";
import { loadWizardState, saveWizardDraft } from "@/lib/registrationCart";
import { searchRlTeams, createRlTeam, fetchMyRlTeams, RlAuthRequiredError } from "@/lib/rlTeams";
import { useAuth } from "@/contexts/AuthContext";
import { Link, useSearchParams } from "react-router-dom";
import { z } from "zod";
import {
  parseTeamName,
  normalizeForMatch,
  stripGenderWords,
  type ParsedTeamName,
} from "@/lib/teamNameParser";

const sb = supabase as any;

function money(n: number) {
  return `$${(n || 0).toFixed(2)}`;
}

// Team home ZIP: exactly 5 digits. Used to tell if a team is local enough to
// be exempt from stay-to-play hotel requirements.
const homeZipSchema = z.string().trim().regex(/^\d{5}$/);
export function isValidHomeZip(zip: string | null | undefined): boolean {
  return homeZipSchema.safeParse(zip).success;
}

// A team is "complete" once it has an age group/division and a resolved team
// (existing squad or a named new one). Incomplete teams never show in the cart.
export function isItemComplete(i: CartItem): boolean {
  if (!i.catalog_division_id || !i.matched_via) return false;
  return i.matched_via === "existing_squad"
    ? !!i.team_id
    : !!(i.team_display_name || i.team_name);
}

export type DivisionOption = {
  catalog_division_id: string;
  gender: "boys" | "girls";
  label: string; // e.g. "17u - Boys"
  ageGroup: string; // e.g. "17u"
  price: number;
};

export type PreviousTeam = {
  team_id: string;
  display_name: string;
  age_group: string | null;
  gender: string | null;
  club_id: string | null;
  aliases: string[];
  event_count: number;
};

/**
 * Standing rule: a stored name never carries an age group, grad year, grade or
 * gender word — even when the coach typed one into the club/variation editor.
 */
export function buildDisplayName(club: string, variation?: string | null) {
  return stripGenderWords(
    [club, variation].filter(Boolean).join(" ").replace(/\s+/g, " ").trim()
  );
}

/**
 * Fuzzy name comparison for the duplicate warning. Case- and
 * punctuation-insensitive (normalizeForMatch), then a Dice coefficient over
 * character bigrams so "Turner Hoops 14U" still catches "Turner Hoops".
 */
function bigrams(s: string): string[] {
  const out: string[] = [];
  for (let i = 0; i < s.length - 1; i++) out.push(s.slice(i, i + 2));
  return out;
}

export function nameSimilarity(a: string, b: string): number {
  const x = normalizeForMatch(a || "");
  const y = normalizeForMatch(b || "");
  if (!x || !y) return 0;
  if (x === y) return 1;
  if (x.includes(y) || y.includes(x)) return 0.9;
  const bx = bigrams(x);
  const by = bigrams(y);
  if (bx.length === 0 || by.length === 0) return 0;
  const pool = new Map<string, number>();
  for (const g of by) pool.set(g, (pool.get(g) ?? 0) + 1);
  let hits = 0;
  for (const g of bx) {
    const n = pool.get(g) ?? 0;
    if (n > 0) {
      hits++;
      pool.set(g, n - 1);
    }
  }
  return (2 * hits) / (bx.length + by.length);
}

/**
 * Search-and-match text input. Always offers existing values before letting the
 * coach create a brand new one, so corrections can't silently spawn duplicates.
 */
function MatchInput({
  value,
  onChange,
  onPick,
  suggestions,
  placeholder,
  label,
  createLabel,
  autoFocus,
  emptyHint,
}: {
  value: string;
  onChange: (v: string) => void;
  onPick?: (v: string) => void;
  suggestions: string[];
  placeholder?: string;
  label: string;
  createLabel: (v: string) => string;
  autoFocus?: boolean;
  emptyHint?: string;
}) {
  const [open, setOpen] = useState(false);
  const q = normalizeForMatch(value.trim());
  const ranked = useMemo(() => {
    const uniq = Array.from(
      new Map(suggestions.filter(Boolean).map((s) => [normalizeForMatch(s), s])).values()
    );
    if (!q) return uniq.slice(0, 8);
    const scored = uniq
      .map((s) => {
        const n = normalizeForMatch(s);
        let score = 0;
        if (n === q) score = 100;
        else if (n.startsWith(q)) score = 80;
        else if (n.includes(q) || q.includes(n)) score = 60;
        else {
          // loose: share the first 3 characters or most tokens
          const tokens = q.split(" ").filter(Boolean);
          const hits = tokens.filter((t) => n.includes(t.slice(0, Math.max(3, t.length - 1)))).length;
          if (hits > 0) score = 30 + hits;
          else if (n.slice(0, 3) === q.slice(0, 3)) score = 20;
        }
        return { s, score };
      })
      .filter((x) => x.score > 0)
      .sort((a, b) => b.score - a.score);
    return scored.slice(0, 8).map((x) => x.s);
  }, [suggestions, q]);

  const exact = ranked.some((s) => normalizeForMatch(s) === q);

  return (
    <div>
      <Label>{label}</Label>
      <Input
        className="mt-1"
        value={value}
        autoFocus={autoFocus}
        placeholder={placeholder}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
      />
      {open && (ranked.length > 0 || (!!value.trim() && !exact)) && (
        <div className="mt-2 space-y-1">
          {ranked.map((s) => (
            <button
              key={s}
              type="button"
              className="w-full text-left px-3 py-2 rounded-md border text-sm hover:bg-muted"
              onClick={() => {
                onChange(s);
                onPick?.(s);
                setOpen(false);
              }}
            >
              {s}
            </button>
          ))}
          {!!value.trim() && !exact && (
            <button
              type="button"
              className="w-full text-left px-3 py-2 rounded-md border border-dashed text-sm text-muted-foreground hover:bg-muted"
              onClick={() => setOpen(false)}
            >
              {createLabel(value.trim())}
            </button>
          )}
        </div>
      )}
      {emptyHint && (
        <p className="text-xs text-muted-foreground mt-1">{emptyHint}</p>
      )}
    </div>
  );
}


export default function TeamNameEntry({
  item,
  options,
  optionKey,
  clubs,
  registeredTeams,
  eventYear,
  updateItem,
  onCommit,
  onDiscard,
  showPrice = true,
  successMessage = (n: string) => `${n} added to your cart`,
  allowRemove = true,
  showVariationOnLowConfidence = false,
  createOnRecruitLook = true,

}: {
  item: CartItem;
  options: DivisionOption[];
  optionKey: (o: DivisionOption) => string;
  clubs: Array<{ id: string; name: string }>;
  registeredTeams?: Map<string, { name: string; registration_id: string }>;
  eventYear: number;
  updateItem: (patch: Partial<CartItem>) => void;
  onCommit: (patch: Partial<CartItem>) => void;
  onDiscard: () => void;
  /** Organiser contexts (Live Sheet) have no fee to quote. */
  showPrice?: boolean;
  successMessage?: (name: string) => string;
  allowRemove?: boolean;
  /** Show a Variation field on the low-confidence ("Which age group?") path.
   *  Default false keeps the public registration wizard unchanged. */
  showVariationOnLowConfidence?: boolean;
  /** Create the team on RecruitLook when the coach confirms a new team.
   *  The organiser's tracked-team list opts out — it records a name only. */
  createOnRecruitLook?: boolean;

}) {
  // When an already-committed team is reopened via the pencil, rehydrate its
  // saved values instead of re-parsing (which loses the division).
  const savedComplete = isItemComplete(item);
  const savedClub =
    item.club_name ||
    item.new_club_name ||
    (item.team_display_name || item.team_name || "")
      .replace(new RegExp(`\\s*${(item.new_short_name || "___nope___").replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s*$`, "i"), "")
      .trim() ||
    item.team_name ||
    "";

  const entryRef = useRef<HTMLDivElement | null>(null);
  const [text, setText] = useState<string>(() => {
    const saved = (item as any).raw_team_input ?? item.team_display_name ?? "";
    if (saved) return saved;
    return loadWizardState()?.drafts?.[item.client_id] ?? "";
  });

  // Keep the in-progress team name across tab switches / remounts.
  useEffect(() => {
    saveWizardDraft(item.client_id, text.trim());
  }, [item.client_id, text]);

  const { user } = useAuth();
  const [matches, setMatches] = useState<PreviousTeam[]>([]);

  // ---- Team home ZIP (local-team hotel exemption) -------------------------
  const [zip, setZip] = useState<string>(() => (item as any).team_home_zip ?? "");
  const [zipTouched, setZipTouched] = useState(false);
  const zipValid = isValidHomeZip(zip);
  const zipEditedRef = useRef(Boolean(item.team_home_zip));
  const zipRequestRef = useRef(0);
  useEffect(() => () => { zipRequestRef.current += 1; }, []);

  // Pre-fill from the team's last registration when one exists.
  const prefillZip = async (teamId: string | null | undefined) => {
    if (!teamId || String(teamId).startsWith("tracked:")) return;
    const request = ++zipRequestRef.current;
    try {
      const { data } = await sb
        .from("event_registrations_v2")
        .select("team_home_zip")
        .eq("team_id", teamId)
        .not("team_home_zip", "is", null)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (request === zipRequestRef.current && !zipEditedRef.current && isValidHomeZip(data?.team_home_zip)) {
        const savedZip = data.team_home_zip.trim();
        setZip(savedZip);
        updateItem({ team_home_zip: savedZip });
      }
    } catch {
      /* pre-fill is best-effort */
    }
  };

  const zipField = (
    <div>
      <Label htmlFor={`team-zip-${item.client_id}`}>Team home ZIP</Label>
      <Input
        id={`team-zip-${item.client_id}`}
        className="mt-1 w-full"
        value={zip}
        inputMode="numeric"
        autoComplete="postal-code"
        maxLength={5}
        required
        pattern="[0-9]{5}"
        aria-describedby={`team-zip-help-${item.client_id}`}
        aria-invalid={zipTouched && !zipValid}
        placeholder="e.g. 66213"
        onChange={(e) => {
          const value = e.target.value.replace(/\D/g, "").slice(0, 5);
          zipEditedRef.current = true;
          setZip(value);
          updateItem({ team_home_zip: value });
        }}
        onBlur={() => setZipTouched(true)}
      />
      <p id={`team-zip-help-${item.client_id}`} className="text-xs text-muted-foreground mt-1">
        Where the team is based. Used for hotel requirements.
      </p>
      {zipTouched && !zipValid && (
        <p className="text-xs font-medium text-destructive mt-1">
          Enter the 5-digit ZIP code where your team is based.
        </p>
      )}
    </div>
  );

  const [matchesDismissed, setMatchesDismissed] = useState(false);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [needsSignIn, setNeedsSignIn] = useState(false);
  const [creating, setCreating] = useState(false);
  const [retryTick, setRetryTick] = useState(0);

  // ---- The coach's own teams --------------------------------------------
  // The registration picker defaults to these: they are what a coach picks
  // almost every time, and a team that isn't theirs must never be selectable.
  // The organiser's "Add teams" dialog opts out (createOnRecruitLook=false) —
  // a director legitimately adds other people's teams.
  const scopeToOwnTeams = createOnRecruitLook && !!user;
  const [myTeams, setMyTeams] = useState<PreviousTeam[]>([]);
  const [loadingMyTeams, setLoadingMyTeams] = useState(false);
  // True once the "Your teams" lookup has settled (rows found or none) — the
  // ?team= preselect must wait for that answer before deciding.
  const myTeamsLoadedRef = useRef(false);

  useEffect(() => {
    if (!scopeToOwnTeams) {
      setMyTeams([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingMyTeams(true);
      try {
        const data = await fetchMyRlTeams();
        if (cancelled) return;
        setMyTeams(
          data.map((t: any) => ({
            team_id: String(t.id),
            display_name: t.display_name || t.name,
            age_group: t.age_group ?? null,
            gender: t.gender ?? null,
            club_id: t.club_id ?? null,
            aliases: [],
            event_count: 0,
          }))
        );
      } catch (err) {
        console.error("fetchMyRlTeams failed", err);
        if (!cancelled) setMyTeams([]);
      } finally {
        if (!cancelled) {
          myTeamsLoadedRef.current = true;
          setLoadingMyTeams(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [scopeToOwnTeams, user?.id]);

  const ownedIds = useMemo(
    () => new Set(myTeams.map((t) => String(t.team_id))),
    [myTeams]
  );
  const isOwnTeam = (id: string) =>
    !scopeToOwnTeams || ownedIds.has(String(id));

  // Duplicate-name warning shown before a brand new team is created.
  const [dupCandidates, setDupCandidates] = useState<PreviousTeam[] | null>(null);
  const [pendingCreate, setPendingCreate] = useState<
    { club: string; variation: string | null; option: DivisionOption } | null
  >(null);
  const [checkingDupes, setCheckingDupes] = useState(false);




  const [parsed, setParsed] = useState<ParsedTeamName | null>(() =>
    savedComplete && item.matched_via !== "existing_squad"
      ? {
          club: savedClub,
          variation: item.new_short_name ?? null,
          ageGroup: item.age_group ?? null,
          confidence: "high",
          assumption: null,
        }
      : null
  );
  const [pickedTeam, setPickedTeam] = useState<PreviousTeam | null>(() =>
    savedComplete && item.matched_via === "existing_squad" && item.team_id
      ? {
          team_id: item.team_id,
          display_name: item.team_display_name || item.team_name,
          age_group: item.age_group ?? null,
          gender: item.gender ?? null,
          club_id: item.club_id ?? null,
          aliases: [],
          event_count: 0,
        }
      : null
  );
  const [optKey, setOptKey] = useState<string>(
    item.catalog_division_id ? `${item.catalog_division_id}:${item.gender}` : ""
  );
  const [editing, setEditing] = useState(false);
  const [editClub, setEditClub] = useState(savedComplete ? savedClub : "");
  const [editVariation, setEditVariation] = useState(
    savedComplete ? item.new_short_name ?? "" : ""
  );
  const [editOrigin, setEditOrigin] = useState<string>("");
  const [variationOptions, setVariationOptions] = useState<string[]>([]);
  // Variation typed on the low-confidence confirmation path ("Which age group?").
  const [lowConfidenceVariation, setLowConfidenceVariation] = useState("");



  const clubNames = useMemo(() => clubs.map((c) => c.name), [clubs]);

  // A team can hold only one active registration per event.
  const alreadyRegistered = (teamId?: string | null) =>
    !!teamId && !!registeredTeams?.has(teamId);
  const blockIfRegistered = (teamId: string | null | undefined, label: string) => {
    if (!alreadyRegistered(teamId)) return false;
    toast.error(`${label} is already registered for this event.`);
    return true;
  };
  const selectedOption =
    options.find((o) => optionKey(o) === optKey) ?? null;

  // ---- Search RecruitLook's teams as they type ---------------------------
  // RecruitLook owns teams; there is no local table to fall back to, so a
  // failure is shown plainly rather than quietly returning nothing.
  // The directory is signed-in only, so a signed-out visitor never searches —
  // they can still type a new team name and continue.
  useEffect(() => {
    if (parsed || pickedTeam || editing) return;
    const q = text.trim();
    if (q.length < 3) {
      setMatches([]);
      setSearchError(null);
      setNeedsSignIn(false);
      return;
    }
    if (!user) {
      setMatches([]);
      setSearchError(null);
      setNeedsSignIn(true);
      return;
    }
    setNeedsSignIn(false);
    let cancelled = false;
    const t = setTimeout(async () => {
      setSearching(true);
      setSearchError(null);
      try {

        const nq = normalizeForMatch(q);
        const rlTeams = await searchRlTeams(q, 8);
        if (cancelled) return;

        const list: PreviousTeam[] = rlTeams.map((r) => ({
          team_id: r.id,
          display_name: r.display_name || r.name,
          age_group: r.age_group,
          gender: r.gender,
          club_id: r.club_id,
          aliases: r.aliases ?? [],
          event_count: r.event_count ?? 0,
        }));


        // Teams the organiser is already tracking for this event. Showing them
        // here stops a coach creating a parallel record for a team that's
        // already on the organiser's list.
        try {
          const { data: trackedHits } = await (sb as any).rpc(
            "find_event_tracked_matches",
            { _event_id: item.event_id, _q: q }
          );
          for (const t of (trackedHits ?? []) as any[]) {
            const onList = t.master_team_id
              ? list.find((l) => l.team_id === t.master_team_id)
              : null;
            if (onList) {
              (onList as any).__tracking_id = t.id;
              (onList as any).__tracked = true;
              continue;
            }
            list.unshift({
              team_id: t.master_team_id ?? `tracked:${t.id}`,
              display_name: t.team_name,
              age_group: t.division_label ?? null,
              gender: t.gender ?? null,
              club_id: null,
              aliases: [],
              event_count: 0,
              __tracking_id: t.id,
              __tracked: true,
            } as any);
          }
        } catch (err) {
          console.error("tracked-team match lookup failed", err);
        }

        if (!cancelled) { setMatches(list); setMatchesDismissed(false); }
      } catch (err: any) {
        if (!cancelled) {
          setMatches([]);
          // The server gate is authoritative — if it says sign in, say so
          // rather than showing this as an outage.
          if (err instanceof RlAuthRequiredError || err?.code === "auth_required") {
            setNeedsSignIn(true);
            setSearchError(null);
          } else {
            setSearchError(
              err?.message ||
                "We couldn't reach RecruitLook to look your team up. Try again in a moment."
            );
          }
        }
      } finally {
        if (!cancelled) setSearching(false);
      }

    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [text, parsed, pickedTeam, editing, item.event_id, retryTick, user]);


  // The tracked row this team came from, if the coach picked one. Every commit
  // carries it so the server can link the registration back to that row.
  const trackingIdRef = useRef<string | null>((item as any).tracking_id ?? null);

  // ---- Helpers -----------------------------------------------------------
  // Exact matches only — we never substitute a nearby age group.
  const exactOptionsForAge = (age: string | null) => {
    if (!age) return [];
    const n = normalizeForMatch(age);
    return options.filter((o) => normalizeForMatch(o.ageGroup) === n);
  };
  const optionsForAge = (age: string | null) => {
    const hit = exactOptionsForAge(age);
    return hit.length > 0 ? hit : options;
  };

  const divisionPatch = (o: DivisionOption) => ({
    catalog_division_id: o.catalog_division_id,
    gender: o.gender,
    division_label: o.label,
    base_price: o.price,
    age_group: normalizeForMatch(o.ageGroup),
  });

  /**
   * Warn before creating — never block. A coach can still create the team,
   * it just has to be a decision rather than an accident.
   */
  const commitNewTeam = async (
    rawClub: string,
    rawVariation: string | null,
    o: DivisionOption
  ) => {
    if (!zipValid) { setZipTouched(true); return; }
    if (!createOnRecruitLook || !user) {
      return performCommitNewTeam(rawClub, rawVariation, o);
    }
    const candidateName = buildDisplayName(
      stripGenderWords(rawClub),
      rawVariation ? stripGenderWords(rawVariation) || null : null
    );
    setCheckingDupes(true);
    try {
      const hits = await searchRlTeams(candidateName, 10);
      const similar = hits
        .filter((r) => nameSimilarity(candidateName, r.display_name || r.name) >= 0.55)
        .slice(0, 5)
        .map((r) => ({
          team_id: r.id,
          display_name: r.display_name || r.name,
          age_group: r.age_group,
          gender: r.gender,
          club_id: r.club_id,
          aliases: r.aliases ?? [],
          event_count: r.event_count ?? 0,
          club_name: r.club_name,
        })) as PreviousTeam[];
      if (similar.length > 0) {
        setDupCandidates(similar);
        setPendingCreate({ club: rawClub, variation: rawVariation, option: o });
        return;
      }
    } catch (err) {
      // A lookup outage must never stop a coach registering.
      console.error("duplicate check failed", err);
    } finally {
      setCheckingDupes(false);
    }
    return performCommitNewTeam(rawClub, rawVariation, o);
  };

  const performCommitNewTeam = async (
    rawClub: string,
    rawVariation: string | null,
    o: DivisionOption
  ) => {
    if (!zipValid) { setZipTouched(true); return; }
    // The club and variation are stored alongside the name, so they get the
    // same treatment — no gender words anywhere.
    const club = stripGenderWords(rawClub);
    const variation = rawVariation ? stripGenderWords(rawVariation) || null : null;
    const name = buildDisplayName(club, variation);
    const clubRow = clubs.find(
      (c) => normalizeForMatch(c.name) === normalizeForMatch(club)
    );


    // New teams are created on RecruitLook, which owns them. RL returns the
    // existing record when one already matched, so this can't make a duplicate.
    // A failure must never produce a team-less registration — we stop here and
    // tell the coach.
    if (createOnRecruitLook) {
      let created: any = null;
      try {
        setCreating(true);
        created = await createRlTeam({
          name,
          age_group: o.ageGroup,
          gender: o.gender,
          raw_input: text.trim(),
        });
      } catch (err: any) {
        toast.error(
          err?.message ||
            "We couldn't reach RecruitLook to add your team. Please try again."
        );
        return;
      } finally {
        setCreating(false);
      }

      const finalName = created.display_name || created.name || name;
      if (blockIfRegistered(created.id, finalName)) return;

      onCommit({
        ...divisionPatch(o),
        team_id: created.id,
        recruitlook_team_id: created.id,
        club_id: created.club_id ?? clubRow?.id ?? null,
        club_name: club,
        new_club_name: null,
        new_short_name: variation || null,
        team_display_name: finalName,
        team_name: finalName,
        matched_via: "existing_squad",
        match_score: 1,
        rejected_match_ids: [],
        raw_team_input: text.trim(),
        team_home_zip: zip.trim() || null,
        tracking_id: trackingIdRef.current,
      } as any);
      toast.success(successMessage(finalName));
      return;
    }

    // Tracked-team entry (the organiser's own list) doesn't create anything on
    // RecruitLook — it only records a name the director typed.
    onCommit({
      ...divisionPatch(o),
      team_id: null,
      recruitlook_team_id: null,
      club_id: clubRow?.id ?? null,
      club_name: club,
      new_club_name: clubRow ? null : club,
      new_short_name: variation || null,
      team_display_name: name,
      team_name: name,
      matched_via: clubRow ? "new_squad" : "new_club",
      match_score: null,
      rejected_match_ids: [],
      raw_team_input: text.trim(),
      team_home_zip: zip.trim() || null,
      tracking_id: trackingIdRef.current,
    } as any);
    toast.success(successMessage(name));
  };


  const commitExistingTeam = (t: PreviousTeam, o: DivisionOption) => {
    if (!zipValid) { setZipTouched(true); return; }
    if (blockIfRegistered(t.team_id, t.display_name)) return;
    onCommit({
      ...divisionPatch(o),
      team_id: t.team_id,
      recruitlook_team_id: String(t.team_id).startsWith("tracked:") ? null : t.team_id,
      club_id: t.club_id ?? null,
      new_club_name: null,
      new_short_name: null,
      team_display_name: t.display_name,
      team_name: t.display_name,
      matched_via: "existing_squad",
      match_score: 1,
      raw_team_input: text.trim(),
      team_home_zip: zip.trim() || null,
      tracking_id: (t as any).__tracking_id ?? trackingIdRef.current,
    } as any);
    toast.success(successMessage(t.display_name));
  };


  const pickTeam = (t: PreviousTeam) => {
    // Remember the organiser's tracked row so the registration links to it
    // rather than becoming a parallel record.
    trackingIdRef.current = (t as any).__tracking_id ?? null;

    // A tracked row with no team record behind it can't be committed as an
    // existing squad — run it through the parser instead, keeping the link.
    if ((t as any).__tracked && String(t.team_id).startsWith("tracked:")) {
      setText(t.display_name);
      setMatches([]);
      setTimeout(() => runParse(true), 0);
      return;
    }

    if (blockIfRegistered(t.team_id, t.display_name)) return;
    prefillZip(t.team_id);
    const candidates = optionsForAge(t.age_group).filter((o) =>
      t.gender ? o.gender === (String(t.gender).toLowerCase().startsWith("g") ? "girls" : "boys") : true
    );
    if (t.age_group && candidates.length === 1 && zipValid) {
      commitExistingTeam(t, candidates[0]);
      return;
    }
    setPickedTeam(t);
    setOptKey(candidates.length === 1 ? optionKey(candidates[0]) : "");
  };


  // ---- Preselect from ?team=<RecruitLook team id> ------------------------
  // RecruitLook sends coaches to the team step with their team already named.
  // The param is read once on mount; when it matches a row in "Your teams" it
  // goes through the very same pickTeam a click uses (division matching,
  // one-division auto-commit and the already-registered block included), then
  // leaves the URL so a refresh can't pick the team a second time.
  const [searchParams, setSearchParams] = useSearchParams();
  const [presetTeamId] = useState(() => (searchParams.get("team") ?? "").trim());
  const presetAppliedRef = useRef(false);

  useEffect(() => {
    if (!presetTeamId || presetAppliedRef.current) return;
    if (loadingMyTeams || !myTeamsLoadedRef.current) return;
    // A blank slot only: never overwrite a team the coach picked, typed or has
    // already committed.
    if (
      savedComplete ||
      ((item as any).raw_team_input ?? "").trim() ||
      (item.team_display_name ?? "").trim() ||
      (item.team_name ?? "").trim() ||
      text.trim()
    )
      return;
    const row = myTeams.find((t) => String(t.team_id) === presetTeamId);
    // Not one of theirs, or the lookup came back empty — the coach simply sees
    // the normal team step. Never an error for this.
    if (!row) return;
    presetAppliedRef.current = true;
    pickTeam(row);
    const next = new URLSearchParams(searchParams);
    next.delete("team");
    setSearchParams(next, { replace: true });
  }, [presetTeamId, myTeams, loadingMyTeams, savedComplete, item, text, pickTeam, searchParams, setSearchParams]);


  const runParse = (silent = false) => {
    const q = text.trim();
    if (q.length < 2) {
      if (!silent) toast.error("Type your team name first");
      return;
    }
    const p = parseTeamName(q, {
      eventYear,
      existingClubs: clubNames,
    });
    setParsed(p);
    const candidates = exactOptionsForAge(p.ageGroup);
    setOptKey(candidates.length === 1 ? optionKey(candidates[0]) : "");
    setEditClub(p.club);
    setEditVariation(p.variation ?? "");
    setLowConfidenceVariation(p.variation ?? "");
  };

  // NOTE: the parse/confirmation deliberately never fires on a typing pause.
  // It only runs when the coach signals they're done — Enter, blur, or the
  // Continue button. Live match search (above) keeps running as they type.



  const startOver = () => {
    setText("");
    setParsed(null);
    setPickedTeam(null);
    setMatches([]);
    setMatchesDismissed(false);
    setSearching(false);
    setEditing(false);
    setOptKey("");
    setEditClub("");
    setEditVariation("");
    setLowConfidenceVariation("");
    setZip("");
    setZipTouched(false);
    zipEditedRef.current = false;
    zipRequestRef.current += 1;
    trackingIdRef.current = null;
    // Also wipe anything already written onto the in-progress cart item so the
    // old parse can't reappear from item state.
    updateItem({
      team_id: null,
      club_id: null,
      club_name: null,
      new_club_name: null,
      new_short_name: null,
      team_display_name: "",
      team_name: "",
      catalog_division_id: "",
      division_label: "",
      base_price: 0,
      matched_via: null,
      match_score: null,
      raw_team_input: null,
      team_home_zip: null,
      age_group: null,
      tracking_id: null,
    } as any);
  };

  // ---- "Change something" — prefill from whatever we worked out ----------
  const openEditor = () => {
    let club = "";
    let variation = "";
    let age: string | null = null;

    if (parsed) {
      club = parsed.club || "";
      variation =
        (!parsed.ageGroup ? lowConfidenceVariation : parsed.variation) ?? "";
      age = parsed.ageGroup;
    } else if (pickedTeam) {
      const clubRow = clubs.find((c) => c.id === pickedTeam.club_id);
      const full = pickedTeam.display_name || "";
      if (clubRow && normalizeForMatch(full).startsWith(normalizeForMatch(clubRow.name))) {
        club = clubRow.name;
        variation = full.slice(clubRow.name.length).trim();
      } else {
        club = full;
      }
      age = pickedTeam.age_group ?? null;
    } else {
      club = editClub;
      variation = editVariation;
    }

    setEditClub(club);
    setEditVariation(variation);
    if (!optKey) {
      const cands = exactOptionsForAge(age);
      if (cands.length === 1) setOptKey(optionKey(cands[0]));
    }
    setEditOrigin(
      text.trim() ||
        buildDisplayName(club, variation) +
          (age ? ` ${age}` : "")
    );
    setEditing(true);
  };

  // Variations already used by this club — so an existing "Red" gets reused.
  useEffect(() => {
    if (!editing) return;
    const club = editClub.trim();
    if (club.length < 2) {
      setVariationOptions([]);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const clubRow = clubs.find(
          (c) => normalizeForMatch(c.name) === normalizeForMatch(club)
        );
        let query = sb
          .from("teams")
          .select("display_name,canonical_name,club_id,age_group")
          .eq("status", "active")
          .is("archived_at", null)
          .limit(200);
        query = clubRow
          ? query.eq("club_id", clubRow.id)
          : query.ilike("display_name", `${club}%`);
        const { data } = await query;
        const out = new Set<string>();
        for (const r of (data ?? []) as any[]) {
          const full = String(r.display_name || r.canonical_name || "").trim();
          if (!full) continue;
          let rest = full;
          if (normalizeForMatch(full).startsWith(normalizeForMatch(club))) {
            rest = full.slice(club.length).trim();
          } else {
            const parts = full.split(/\s+/);
            rest = parts.slice(1).join(" ");
          }
          // drop any trailing age token like "12u" / "u12" / "2028"
          rest = rest
            .replace(/\b(\d{1,2}\s?u|u\s?\d{1,2}|20\d{2})\b/gi, "")
            .replace(/\s+/g, " ")
            .trim();
          if (rest.length > 0 && rest.length <= 40) out.add(rest);
        }
        if (!cancelled) setVariationOptions(Array.from(out).sort());
      } catch {
        if (!cancelled) setVariationOptions([]);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [editing, editClub, clubs]);

  // The match list — rendered identically wherever matches exist, always with
  // "None of these, it's a new team" at the bottom, in its own scroll region.
  const showMatches =
    !matchesDismissed && text.trim().length >= 3 && matches.length > 0;

  const renderMatchList = (onNewTeam: () => void) => (
    <div className="space-y-2" data-testid="team-match-block">
      <p className="text-sm text-muted-foreground">
        We found teams that look like yours. Pick one to keep your rosters and
        history together, or add a new team below.
      </p>

      <div
        data-testid="team-match-list"
        className="max-h-[300px] overflow-y-auto overscroll-contain space-y-2 pr-1"
      >
        {matches.map((m) => {
          const taken = alreadyRegistered(m.team_id);
          // Seeing that a team already exists is the point; being able to take
          // it over is the bug. Someone else's team stays visible, not pickable.
          const tracked = !!(m as any).__tracked;
          const notMine = !taken && !tracked && !isOwnTeam(m.team_id);
          const disabled = taken || notMine;
          return (
            <button
              key={m.team_id}
              type="button"
              disabled={disabled}
              aria-disabled={disabled}
              onClick={() => pickTeam(m)}
              className={
                disabled
                  ? "w-full text-left rounded-lg border border-dashed p-4 opacity-60 cursor-not-allowed"
                  : "w-full text-left rounded-lg border p-4 hover:bg-muted active:bg-muted transition-colors"
              }
            >
              <div className="text-base font-semibold">{m.display_name}</div>
              {tracked && !taken && (
                <div className="mt-1 inline-block rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
                  Already on the organiser's list for this event
                </div>
              )}
              <div className="text-sm text-muted-foreground">
                {taken ? (
                  <>Already registered for this event</>
                ) : (
                  <>
                    {[
                      m.age_group || "Age group not set",
                      (m as any).club_name || null,
                      m.event_count > 0
                        ? `${m.event_count} previous event${m.event_count === 1 ? "" : "s"}`
                        : null,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </>
                )}
              </div>
              {notMine && (
                <div className="mt-1 text-xs font-medium text-amber-700">
                  This isn't your team — it's managed by another coach.
                </div>
              )}
            </button>
          );
        })}
      </div>

      <Button
        variant="outline"
        size="lg"
        className="w-full justify-start"
        onClick={onNewTeam}
      >
        <Plus className="w-4 h-4 mr-2" />
        None of these, it's a new team
      </Button>
    </div>
  );


  const ageSelect = (list: DivisionOption[]) => (
    <Select value={optKey} onValueChange={setOptKey}>
      <SelectTrigger className="h-11">
        <SelectValue placeholder="Select an age group" />
      </SelectTrigger>
      <SelectContent>
        {list.length === 0 && (
          <div className="px-3 py-2 text-sm text-muted-foreground">
            No active divisions for this event
          </div>
        )}
        {list.map((o) => (
          <SelectItem key={optionKey(o)} value={optionKey(o)}>
            {o.label}{showPrice ? ` — ${money(o.price)}` : ""}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  // ---- 0. Duplicate warning before creating a new team -------------------
  if (pendingCreate && dupCandidates && dupCandidates.length > 0) {
    const proposed = buildDisplayName(pendingCreate.club, pendingCreate.variation);
    return (
      <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 space-y-3">
        <div>
          <p className="text-base font-semibold text-amber-900">
            {dupCandidates.length} team
            {dupCandidates.length === 1 ? "" : "s"} already exist
            {dupCandidates.length === 1 ? "s" : ""} with a similar name. Is one
            of these yours?
          </p>
          <p className="text-sm text-amber-900/80 mt-0.5">
            You typed <strong>{proposed}</strong>.
          </p>
        </div>
        <div className="space-y-2 max-h-[260px] overflow-y-auto pr-1">
          {dupCandidates.map((m) => {
            const taken = alreadyRegistered(m.team_id);
            const notMine = !taken && !isOwnTeam(m.team_id);
            const disabled = taken || notMine;
            return (
              <button
                key={m.team_id}
                type="button"
                disabled={disabled}
                aria-disabled={disabled}
                onClick={() => {
                  setDupCandidates(null);
                  setPendingCreate(null);
                  pickTeam(m);
                }}
                className={
                  disabled
                    ? "w-full text-left rounded-lg border border-dashed bg-background p-3 opacity-60 cursor-not-allowed"
                    : "w-full text-left rounded-lg border bg-background p-3 hover:bg-muted transition-colors"
                }
              >
                <div className="text-sm font-semibold">{m.display_name}</div>
                <div className="text-xs text-muted-foreground">
                  {[m.age_group || "Age group not set", (m as any).club_name || null]
                    .filter(Boolean)
                    .join(" · ")}
                </div>
                {taken && (
                  <div className="text-xs text-muted-foreground mt-0.5">
                    Already registered for this event
                  </div>
                )}
                {notMine && (
                  <div className="text-xs font-medium text-amber-700 mt-0.5">
                    This isn't your team — it's managed by another coach.
                  </div>
                )}
              </button>
            );
          })}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            size="lg"
            disabled={creating}
            onClick={() => {
              const p = pendingCreate;
              setDupCandidates(null);
              setPendingCreate(null);
              if (p) performCommitNewTeam(p.club, p.variation, p.option);
            }}
          >
            {creating && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
            None of these — create {proposed}
          </Button>
          <Button
            size="lg"
            variant="outline"
            onClick={() => {
              setDupCandidates(null);
              setPendingCreate(null);
            }}
          >
            Go back
          </Button>
        </div>
      </div>
    );
  }

  // ---- 1. Picked an existing team that still needs an age group ----------
  if (pickedTeam) {
    return (
      <div className="border rounded-lg p-4 space-y-4">
        <div>
          <div className="text-base font-semibold">{pickedTeam.display_name}</div>
          <Label className="mt-3 block">Which age group?</Label>
          <p className="text-xs text-muted-foreground mt-1">
            We couldn't tell which division this team is playing in at this event.
          </p>
        </div>
        {ageSelect(optionsForAge(pickedTeam.age_group))}
        {zipField}
        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="lg"
            disabled={!selectedOption || !zipValid}
            onClick={() => selectedOption && zipValid && commitExistingTeam(pickedTeam, selectedOption)}
          >
            <Check className="w-4 h-4 mr-1" /> Yes, that's us
          </Button>
          <Button size="lg" variant="outline" onClick={openEditor}>
            Change something
          </Button>
          <Button
            variant="link"
            className="px-1 text-muted-foreground"
            onClick={startOver}
          >
            Start over
          </Button>
        </div>

      </div>
    );
  }

  // ---- 2. "Change something" — correct the parse, with matching -----------
  if (editing) {
    return (
      <div className="border rounded-lg p-4 space-y-4">
        <div>
          <p className="text-sm text-muted-foreground">
            You typed: <strong>{editOrigin || "your team"}</strong> — change
            anything that's wrong.
          </p>
        </div>
        <MatchInput
          label="Club"
          value={editClub}
          onChange={setEditClub}

          suggestions={clubNames}
          placeholder="Start typing your club"
          createLabel={(v) => `Create new club "${v}"`}
          autoFocus
          emptyHint="Pick your existing club if it's listed — that keeps your rosters and history together."
        />
        <MatchInput
          label="Variation (optional)"
          value={editVariation}
          onChange={setEditVariation}
          suggestions={variationOptions}
          placeholder="Red, Elite, coach name…"
          createLabel={(v) => `Use new variation "${v}"`}
          emptyHint="Colour, coach name, or circuit — what tells this team apart from the club's other teams at this age."
        />
        <div>
          <Label>Age group</Label>
          <div className="mt-1">{ageSelect(options)}</div>
        </div>
        {zipField}
        <div className="flex gap-2">
          <Button
            disabled={!selectedOption || editClub.trim().length < 2 || !zipValid}
            onClick={() => {
              if (!selectedOption || !zipValid) return;
              commitNewTeam(
                editClub.trim(),
                editVariation.trim() || null,
                selectedOption
              );
            }}
          >
            Save
          </Button>
          <Button variant="ghost" onClick={() => setEditing(false)}>
            Cancel
          </Button>
        </div>
      </div>
    );
  }


  // ---- 3. Confirmation ----------------------------------------------------
  if (parsed) {
    const exactCandidates = exactOptionsForAge(parsed.ageGroup);
    const ageNotOffered = !!parsed.ageGroup && exactCandidates.length === 0;
    const candidates = ageNotOffered ? options : optionsForAge(parsed.ageGroup);
    const needsAge = !selectedOption;
    const isLowConfidence = !parsed.ageGroup;
    const lowConfidenceDisplayName = buildDisplayName(
      parsed.club,
      lowConfidenceVariation
    );
    return (
      <div className="space-y-4">
        {showMatches && renderMatchList(() => setMatchesDismissed(true))}
        <div className="border rounded-lg p-4 space-y-4">
          {zipField}
          {isLowConfidence && showVariationOnLowConfidence ? (
            <>
              <div>
                <div className="text-base font-semibold">
                  We've got: {lowConfidenceDisplayName}
                </div>
                {parsed.assumption && (
                  <p className="text-sm text-muted-foreground mt-1">
                    {parsed.assumption}
                  </p>
                )}
                <div className="mt-4 grid gap-4">
                  <div>
                    <Label>Which age group?</Label>
                    <p className="text-xs text-muted-foreground mt-1">
                      We couldn't work out the age group from what you typed.
                    </p>
                    <div className="mt-2">{ageSelect(candidates)}</div>
                  </div>
                  <div>
                    <Label>Variation (optional)</Label>
                    <p className="text-xs text-muted-foreground mt-1">
                      Colour, coach name, or circuit — what tells this team
                      apart from the club's other teams at this age.
                    </p>
                    <Input
                      className="mt-2"
                      value={lowConfidenceVariation}
                      onChange={(e) =>
                        setLowConfidenceVariation(e.target.value)
                      }
                      placeholder="Red, Elite, coach name…"
                    />
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  size="lg"
                  disabled={!selectedOption || !zipValid}
                  onClick={() =>
                    selectedOption &&
                    zipValid &&
                    commitNewTeam(
                      parsed.club,
                      lowConfidenceVariation.trim() || null,
                      selectedOption
                    )
                  }
                >
                  <Check className="w-4 h-4 mr-1" /> Yes, that's us
                </Button>
                <Button size="lg" variant="outline" onClick={openEditor}>
                  Change something
                </Button>
                <Button
                  variant="link"
                  className="px-1 text-muted-foreground"
                  onClick={startOver}
                >
                  Start over
                </Button>
              </div>
            </>
          ) : needsAge ? (
            <>
              <div>
                <div className="text-base font-semibold">
                  {parsed.ageGroup && !ageNotOffered ? "We've got: " : ""}
                  {buildDisplayName(parsed.club, parsed.variation)}
                  {parsed.ageGroup && !ageNotOffered
                    ? ` · ${parsed.ageGroup}`
                    : ""}
                </div>
                {parsed.assumption && (
                  <p className="text-sm text-muted-foreground mt-1">
                    {parsed.assumption}
                  </p>
                )}
                {ageNotOffered && (
                  <p className="text-sm font-medium text-destructive mt-1">
                    We read that as {parsed.ageGroup}, but this event doesn't
                    have a {parsed.ageGroup} division.
                  </p>
                )}
                <Label className="mt-3 block">
                  {ageNotOffered
                    ? "Pick one of the divisions this event offers"
                    : parsed.ageGroup
                    ? "Which division?"
                    : "Which age group?"}
                </Label>
                <p className="text-xs text-muted-foreground mt-1">
                  {ageNotOffered
                    ? "Choose the division this team will actually play in."
                    : parsed.ageGroup
                    ? "Pick the division this team is playing in."
                    : "We couldn't work out the age group from what you typed."}
                </p>
              </div>
              {ageSelect(candidates)}
              <p className="text-sm text-muted-foreground">
                Pick the division this team will play in, then confirm.
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <Button size="lg" disabled>
                  <Check className="w-4 h-4 mr-1" /> Yes, that's us
                </Button>
                <span className="text-xs text-muted-foreground">
                  Pick a division first.
                </span>

                <Button size="lg" variant="outline" onClick={openEditor}>
                  Change something
                </Button>
                <Button
                  variant="link"
                  className="px-1 text-muted-foreground"
                  onClick={startOver}
                >
                  Start over
                </Button>
              </div>
            </>
          ) : (
            <>
              <div>
                <div className="text-base font-semibold">
                  We've got: {parsed.club}
                  {parsed.variation ? ` — ${parsed.variation}` : ""} ·{" "}
                  {selectedOption.label}
                </div>
                {parsed.assumption && (
                  <p className="text-sm text-muted-foreground mt-1">
                    {parsed.assumption}
                  </p>
                )}
                {showPrice && (
                  <p className="text-sm text-muted-foreground mt-1">
                    Registration fee:{" "}
                    <strong>{money(selectedOption.price)}</strong>
                  </p>
                )}
                <p className="text-sm text-muted-foreground mt-2">
                  Check this is right, then confirm. You can change any part of
                  it.
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <Button
                  size="lg"
                  disabled={!zipValid}
                  onClick={() =>
                    zipValid &&
                    commitNewTeam(parsed.club, parsed.variation, selectedOption)
                  }
                >
                  <Check className="w-4 h-4 mr-1" /> Yes, that's us
                </Button>
                <Button size="lg" variant="outline" onClick={openEditor}>
                  Change something
                </Button>
                <Button
                  variant="link"
                  className="px-1 text-muted-foreground"
                  onClick={startOver}
                >
                  Start over
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // ---- 4. The single question --------------------------------------------
  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="team-name-input" className="sr-only">
          Team name
        </Label>
        <p className="text-sm text-muted-foreground mb-2">
          Type your team name however you normally write it — we'll work out the
          club and age group for you.
        </p>
        <div
          className="mt-1 flex flex-col sm:flex-row gap-2 sm:gap-2"
          ref={entryRef}
        >
          <Input
            id="team-name-input"
            className="h-12 text-base placeholder:text-muted-foreground/50 placeholder:italic"
            value={text}
            enterKeyHint="done"
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                (e.currentTarget as HTMLInputElement).blur();
                runParse();
              }
            }}
            onBlur={(e) => {
              // Don't parse when focus moves to a match card / Continue button.
              const next = e.relatedTarget as Node | null;
              if (next && entryRef.current?.contains(next)) return;
              if (text.trim().length < 2) return;
              runParse(true);
            }}
            placeholder="e.g. Bacon Red 12u"
            autoFocus
          />
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 sm:shrink-0">
            <Button
              type="button"
              size="lg"
              className="h-12 w-full sm:w-auto shrink-0"
              disabled={text.trim().length < 2}
              onClick={() => runParse()}
            >
              Continue
            </Button>
            {text.trim().length < 2 && (
              <span className="text-xs text-muted-foreground sm:whitespace-nowrap">
                Type your team name to continue.
              </span>
            )}
          </div>
        </div>

        <p className="text-xs text-muted-foreground mt-1">
          Press Enter or tap Continue when you've finished typing.
        </p>
      </div>

      {/* Signed in: the coach's own teams, offered before they type anything.
          Signed out there is nothing to fall back on, so it stays search-only. */}
      {scopeToOwnTeams && text.trim().length === 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium">Your teams</p>
          {loadingMyTeams ? (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="w-3 h-3 animate-spin" /> Loading your teams…
            </div>
          ) : myTeams.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              No teams on your account yet — type your team name above.
            </p>
          ) : (
            <div className="max-h-[260px] overflow-y-auto overscroll-contain space-y-2 pr-1">
              {myTeams.map((m) => {
                const taken = alreadyRegistered(m.team_id);
                return (
                  <button
                    key={m.team_id}
                    type="button"
                    disabled={taken}
                    aria-disabled={taken}
                    onClick={() => pickTeam(m)}
                    className={
                      taken
                        ? "w-full text-left rounded-lg border border-dashed p-3 opacity-60 cursor-not-allowed"
                        : "w-full text-left rounded-lg border p-3 hover:bg-muted active:bg-muted transition-colors"
                    }
                  >
                    <div className="text-base font-semibold">{m.display_name}</div>
                    <div className="text-sm text-muted-foreground">
                      {taken
                        ? "Already registered for this event"
                        : m.age_group || "Age group not set"}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}


      {!user && needsSignIn && (
        <div className="rounded-lg border bg-muted/40 p-3 text-sm">
          <p className="font-medium">Sign in to find your team</p>
          <p className="text-muted-foreground text-xs mt-0.5">
            Team lookup is only available to signed-in coaches. You can still
            type a new team name and continue.
          </p>
          <Button asChild variant="outline" size="sm" className="mt-2">
            <Link
              to={`/login?redirect=${encodeURIComponent(
                window.location.pathname + window.location.search
              )}`}
            >
              Sign in
            </Link>
          </Button>
        </div>
      )}

      {searching && (

        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="w-3 h-3 animate-spin" /> Looking for your team…
        </div>
      )}

      {!searching && searchError && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          <p>{searchError}</p>
          <Button
            variant="outline"
            size="sm"
            className="mt-2"
            onClick={() => setRetryTick((n) => n + 1)}
          >
            Try again
          </Button>
        </div>
      )}


      {showMatches && renderMatchList(() => {
        setMatchesDismissed(true);
        runParse();
      })}



      {allowRemove && isItemComplete(item) && (
        <div className="flex justify-end border-t pt-4">
          <Button variant="ghost" size="sm" onClick={onDiscard}>
            <Trash2 className="w-4 h-4 mr-1" /> Remove this team
          </Button>
        </div>
      )}
    </div>
  );
}
